import {
  Box,
  List as MuiList,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Collapse,
} from "@mui/material";
import styled from "styled-components";
import {
  KeyboardArrowUpOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useMemo, useState } from "react";
import { BiRadioCircle } from "react-icons/bi";
import { RxDividerVertical } from "react-icons/rx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useNavigate, useLocation } from "react-router-dom";

import sidebarRoutes from "./sidebarRoutes";
import { colors } from "../../../pages/css components/Style";
import {
  setParentName,
  setSubParentName,
} from "../../../redux/slices/memberSlice";

const Wrapper = styled(Box)`
  flex: 1;
  max-height: 84%;
  padding: 0 15px;
`;

const List = styled(MuiList)`
  padding: 0 0 10px 0;
  margin-top: 20px;
`;

const ListItemButton = styled(MuiListItemButton)`
  display: flex;
  height: 50px;
  border-radius: 0px;
  margin: 7px 0;
  padding: 10px;
  border-bottom: 1px solid ${colors.lightBlue};
  &:hover {
    background-color: ${colors.lightBlue} !important;
  }
`;

const ListItemIcon = styled(MuiListItemIcon)`
  font-size: 25px;
  padding-right: 20px;
  min-width: 20px;
`;

const ListItemText = styled(MuiListItemText)`
  .MuiListItemText-primary {
    font-size: 14px;
    font-weight: 500;
  }
`;

const SubListItemText = styled(MuiListItemText)`
  .MuiListItemText-primary {
    font-size: 14px;
    font-weight: 500;
  }
`;

const getIconStyle = (isActive, singleList = true) => ({
  width: "24px",
  height: "24px",
  color: isActive ? (singleList ? "#fff" : colors.sprintexBlue) : "#fff",
  fill: isActive ? (singleList ? "#fff" : colors.sprintexBlue) : "#fff",
});

export default function Menu({ setSelectedMenu }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const memberDetails = useSelector((state) => state.memberDetails);
  const { dashboardPermissions, agent, parentName, subParentName } =
    memberDetails;

  const initialParent = sessionStorage.getItem("parentName") || parentName;
  const initialSubParent =
    sessionStorage.getItem("subParentName") || subParentName;

  const [folderOpen, setFolderOpen] = useState({
    state: { [initialParent]: true },
    name: initialParent,
  });
  const [subFolderOpen, setSubFolderOpen] = useState({
    state: {
      [initialSubParent]: true,
    },
    name: initialSubParent,
    parentName: initialParent,
  });

  const permittedRoutes = useMemo(() => {
    const routes = [];

    for (let route of sidebarRoutes) {
      let childrenRoutes = [];

      if (route.children) {
        for (let childRoute of route.children) {
          if (dashboardPermissions?.some((page) => page.ID === childRoute.ID)) {
            childrenRoutes.push(childRoute);
          }
        }
        if (childrenRoutes.length > 0) {
          routes.push({ ...route, children: childrenRoutes });
        }
      } else {
        let parent = dashboardPermissions?.find((page) => page.ID === route.ID);
        if (parent) {
          if (parent.ID === "template-list" && agent?.length <= 0) continue;
          routes.push(route);
        }
      }
    }

    return routes;
  }, [dashboardPermissions, agent]);

  useEffect(() => {
    if (sessionStorage.getItem("parentName")) {
      let n = sessionStorage.getItem("parentName");
      setFolderOpen({ state: { [n]: true }, name: n });
    }

    if (sessionStorage.getItem("subParentName")) {
      let n = sessionStorage.getItem("subParentName");
      setSubFolderOpen({ state: { [n]: true }, name: n });
    }
  }, [parentName, subParentName]);

  const ListItem = ({
    name,
    icon: Icon,
    path = "",
    parentName,
    subParentName,
    singleList,
  }) => {
    const getBasePath = (fullPath) => {
      const segments = fullPath.split("/").filter(Boolean);
      return "/" + (segments.length > 0 ? segments[0] : "");
    };

    const match = path && location.pathname.startsWith(getBasePath(path));

    return (
      <ListItemButton
        style={singleBackground(match, singleList)}
        onClick={() => {
          navigate(path);
          setSelectedMenu(path);
          dispatch(setParentName(parentName));
          setFolderOpen({ state: { [parentName]: true }, name: parentName });
          sessionStorage.setItem("parentName", parentName);

          if (singleList) {
            setSubFolderOpen({ state: {}, name: "" });
            dispatch(setSubParentName(""));
          } else {
            setSubFolderOpen({
              state: { [subParentName]: true },
              name: subParentName,
            });
            dispatch(setSubParentName(subParentName));
            sessionStorage.setItem("subParentName", subParentName);
          }
        }}
        selected={!!match}
      >
        <ListItemIcon>
          <Icon {...getIconStyle(!!match, singleList)} />
        </ListItemIcon>
        <SubListItemText
          style={{
            fontWeight: "600",
            textAlign: "start",
            color: "#fff",
          }}
          primary={name}
        />
      </ListItemButton>
    );
  };

  const ListFolder = ({ name, icon: Icon, children }) => {
    const isOpen = folderOpen.state[name];
    const isActive = parentName === name;

    return (
      <Fragment>
        <ListItemButton
          onClick={() =>
            setFolderOpen({
              state: { ...folderOpen.state, [name]: !isOpen },
              name,
            })
          }
          style={withBackground(isActive)}
        >
          <ListItemIcon>
            <Icon {...getIconStyle(isActive)} />
          </ListItemIcon>
          <ListItemText
            style={{
              textAlign: "start",
              color: "#fff",
            }}
            primary={name}
          />
          {isOpen ? (
            <KeyboardArrowUpOutlined style={{ color: colors.sprintexBlue }} />
          ) : (
            <KeyboardArrowDownOutlined style={{ color: colors.sprintexBlue }} />
          )}
        </ListItemButton>

        <Collapse
          in={
            isOpen ||
            (children.length > 0 &&
            !isOpen &&
            folderOpen.state.hasOwnProperty(name)
              ? false
              : children.some((child) => child.name === subFolderOpen.name))
          }
        >
          <List>
            {children.map(({ name: childName, ID, path, icon, subChilds }) =>
              subChilds ? (
                <SubListFolder
                  key={ID}
                  parentName={name}
                  name={childName}
                  children={subChilds}
                  icon={icon}
                />
              ) : (
                <ListItem
                  key={ID}
                  name={childName}
                  path={path}
                  parentName={name}
                  icon={BiRadioCircle}
                  singleList={false}
                />
              )
            )}
          </List>
        </Collapse>
      </Fragment>
    );
  };

  const SubListFolder = ({ parentName, name, icon: Icon, children }) => {
    const isOpen = subFolderOpen.state[name];
    const isActive = subParentName === name;

    return (
      <Fragment>
        <ListItemButton
          onClick={() => {
            setSubFolderOpen({
              state: {
                ...subFolderOpen.state,
                [name]: !isOpen,
                parentName,
              },
              name,
            });
            dispatch(setSubParentName(name));
          }}
          style={withBackground(isActive)}
        >
          <ListItemIcon>
            <Icon {...getIconStyle(isActive)} />
          </ListItemIcon>
          <ListItemText
            style={{
              textAlign: "start",
              color: isActive ? "#fff" : "#fff",
            }}
            primary={name}
          />
          {isOpen ? (
            <KeyboardArrowUpOutlined
              style={{ color: isActive ? "#fff" : "#fff" }}
            />
          ) : (
            <KeyboardArrowDownOutlined
              style={{ color: isActive ? "#fff" : "#fff" }}
            />
          )}
        </ListItemButton>

        <Collapse in={isOpen} style={{ marginInline: "10px" }}>
          <List>
            {children.map(({ name: childName, ID, path }) => (
              <ListItem
                key={ID}
                name={childName}
                path={path}
                parentName={parentName}
                subParentName={name}
                icon={RxDividerVertical}
              />
            ))}
          </List>
        </Collapse>
      </Fragment>
    );
  };

  return (
    <Wrapper>
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <List>
          {permittedRoutes.map(({ name, icon, ID, path, children }) =>
            children ? (
              <ListFolder
                key={ID}
                name={name}
                icon={icon}
                children={children}
              />
            ) : (
              <ListItem
                key={ID}
                name={name}
                icon={icon}
                path={path}
                parentName={name}
                singleList={true}
              />
            )
          )}
        </List>
      </PerfectScrollbar>
    </Wrapper>
  );

  function singleBackground(match, singleList) {
    return {
      backgroundColor:
        match && singleList ? colors.lightBlue : colors.sprintexBlue,
      // marginRight: match && singleList && "10px",
      borderRadius: match && singleList && "0 17px 17px 0",
    };
  }

  function withBackground(isActive) {
    return {
      backgroundColor: isActive ? colors.lightBlue : colors.sprintexBlue,
      // marginRight: isActive && "10px",
      borderRadius: isActive && "0 17px 17px 0",
    };
  }
}
